import { atom, selector, useRecoilState, useRecoilValue } from 'recoil';
import { useQuery } from '@tanstack/react-query';
import { PRODUCT_KEYS } from '@type/productType';
import { CallGetAuth } from '@library/request/callProduct';
import { useEffect } from 'react';

//  메뉴관리 권한 로직은 해당 파일에 추가하여 리코일로 관리 (WMPO-10276)

// 메뉴 권한 상태
const menuPermissionState = atom({
  key: 'menuPermissionState',
  default: { data: [], isFetch: false },
});

// 테스트용
const test = [
  {
    permissionType: 2, // 권한없음:0, 전체권한:1, 수정권한:2
    information: 0, // 정보
    price: 0, // 가격
    display: 0, // 노출
    soldOut: 0, // 품절
    sort: 0, // 순서
    recommendMenu: 1, // 추천메뉴
    origin: 1, // 원산지
  },
];

const createNoAuthSelector = (key: string, condition: (permissions: any) => boolean) =>
  selector({
    key,
    get: ({ get }) => {
      const menuPermission = get(menuPermissionState);
      // const menuPermission = test;
      if (!menuPermission?.data?.length) return false; // 데이터 없으면 false
      return condition(menuPermission?.data[0]);
    },
  });

// 공통 체크 함수
const hasNoAuth = (permissions: any, checkCondition: boolean) => {
  // 전체 권한(1): 활성화 (false 반환 = 권한 없음 아님)
  if (permissions.permissionType === 1) return false;

  // 권한 없음(0): 비활성화 (true 반환 = 권한 없음)
  if (permissions.permissionType === 0) return true;

  // 수정 권한(2): 조건에 따라
  return checkCondition;
};

// 메뉴관리 탭 비노출 유무 (모든 리스트 비노출되며 empty 박스 노출)
// 비노출 조건 : 수정권한 (순서/추천메뉴/원산지) or 권한없음
const isAllMenuNoAuthSelector = createNoAuthSelector('isAllMenuNoAuthSelector', (permissions) => {
  return (
    permissions.permissionType === 0 || // 권한없음:0, 전체권한:1, 수정권한:2
    (permissions.permissionType === 2 &&
      permissions.information === 0 &&
      permissions.price === 0 &&
      permissions.display === 0 &&
      permissions.soldOut === 0 &&
      (permissions.sort === 1 || permissions.recommendMenu === 1 || permissions.origin === 1))
  );
});

// 옵션관리 탭 비노출 유무 (옵션관리 탭의 바텀시트의 버튼 클릭 시 이동하는 페이지들 모두)
// 비노출 조건 : 수정권한 (추천메뉴/원산지) or 권한없음
const isAllOptNoAuthSelector = createNoAuthSelector('isAllOptNoAuthSelector', (permissions) => {
  return (
    permissions.permissionType === 0 || // 권한없음:0, 전체권한:1, 수정권한:2
    (permissions.permissionType === 2 &&
      permissions.information === 0 &&
      permissions.price === 0 &&
      permissions.display === 0 &&
      permissions.soldOut === 0 &&
      permissions.sort === 0 &&
      (permissions.recommendMenu === 1 || permissions.origin === 1))
  );
});

// 메뉴순서 탭 비노출 유무
// 비노출 조건 : 수정권한 (정보/가격/노출/품절/추천메뉴/원산지) or 권한없음
const isAllOrderNoAuthSelector = createNoAuthSelector('isAllOrderNoAuthSelector', (permissions) => {
  return !(
    permissions.permissionType === 1 || // 권한없음:0, 전체권한:1, 수정권한:2
    (permissions.permissionType === 2 && permissions.sort === 1)
  );
});

// 추천메뉴 탭 비노출 유무
// 비노출 조건 :  수정권한 (정보/가격/노출/품절/순서/원산지) or 권한없음
const isAllRecomNoAuthSelector = createNoAuthSelector('isAllRecomNoAuthSelector', (permissions) => {
  return !(
    permissions.permissionType === 1 || // 권한없음:0, 전체권한:1, 수정권한:2
    (permissions.permissionType === 2 && permissions.recommendMenu === 1)
  );
});

// 원산지정보 탭 비노출 유무
// 비노출 조건 : 수정권한 (정보/가격/노출/품절/순서/추천메뉴) or 권한없음
const isAllOriginNoAuthSelector = createNoAuthSelector('isAllOriginNoAuthSelector', (permissions) => {
  return !(
    permissions.permissionType === 1 || // 권한없음:0, 전체권한:1, 수정권한:2
    (permissions.permissionType === 2 && permissions.origin === 1)
  );
});

// 버튼명 > 카테고리 추가 / 카테고리 삭제 / 메뉴추가 / 메뉴삭제
const isNoAuthSelector = createNoAuthSelector('isAuthCase1', (permissions) => {
  // 비활성화 조건 : 수정 권한에서는 항상 비활성화
  return hasNoAuth(
    permissions,
    true
  );
});

// [메뉴관리 탭] 버튼명 > 카테고리 정보 수정, 옵션 연결
const isNoAuthSelector2 = createNoAuthSelector('isAuthCase2', (permissions) => {
  // 비활성화: 정보 수정 권한이 없는 경우
  return hasNoAuth(
    permissions,
    permissions.information !== 1
  );
});

// [메뉴관리 탭] 버튼명 > 메뉴 정보 수정
const isNoAuthSelector3 = createNoAuthSelector('isAuthCase3', (permissions) => {
  // 비활성화: 정보 수정 AND 가격 수정 권한이 모두 없는 경우
  return hasNoAuth(
    permissions,
    permissions.information !== 1 && permissions.price !== 1
  );
});

// [메뉴관리 탭] 버튼명 > 메뉴노출·품절설정
const isNoAuthSelector4 = createNoAuthSelector('isAuthCase4', (permissions) => {
  // 비활성화 : 노출 AND 품절 권한이 모두 없는 경우
  return hasNoAuth(
    permissions,
    permissions.display !== 1 && permissions.soldOut !== 1
  );
});

// [옵션관리 탭] 버튼명 > 옵션 그룹명 수정
const isNoAuthSelector5 = createNoAuthSelector('isAuthCase5', (permissions) => {
  // 비활성화 : 정보 권한이 없는 경우
  return hasNoAuth(
    permissions,
    permissions.information !== 1
  );
});

// [옵션관리 탭] 버튼명 > 옵션그룹 노출 설정
const isNoAuthSelector6 = createNoAuthSelector('isAuthCase6', (permissions) => {
  // 비활성화 : [노출] AND [품절] 권한이 모두 없는 경우
  return hasNoAuth(
    permissions,
    permissions.display !== 1 && permissions.soldOut !== 1
  );
});

// [옵션관리 탭] 버튼명 > 옵션 순서 설정
const isNoAuthSelector7 = createNoAuthSelector('isAuthCase7', (permissions) => {
  // 비활성화 : 순서 권한이 없는 경우
  return hasNoAuth(
    permissions,
    permissions.sort !== 1
  );
});

// [옵션관리 탭] 버튼명 > 연결 메뉴 모두 해제
const isNoAuthSelector8 = createNoAuthSelector('isAuthCase8', (permissions) => {
  // 비활성화 : 정보 권한이 없는 경우
  return hasNoAuth(
    permissions,
    permissions.information !== 1
  );
});

// [옵션관리 탭] 버튼명 > 옵션그룹 복사, 옵션그룹 삭제, 옵션 삭제
const isNoAuthSelector9 = createNoAuthSelector('isAuthCase9', (permissions) => {
  // 비활성화 조건 : 수정 권한에서는 항상 비활성화
  return hasNoAuth(
    permissions,
    true
  );
});

// [옵션관리 탭] 버튼명 > 옵션명 및 가격 수정
const isNoAuthSelector10 = createNoAuthSelector('isAuthCase10', (permissions) => {
  // 비활성화 : 정보 AND 가격 권한이 모두 없는 경우
  return hasNoAuth(
    permissions,
    permissions.information !== 1 && permissions.price !== 1
  );
});

// [옵션관리 탭] 버튼명 > 옵션 노출 · 품절 설정
const isNoAuthSelector11 = createNoAuthSelector('isAuthCase11', (permissions) => {
  // 비활성화 : 노출 AND 품절 권한이 모두 없는 경우
  return hasNoAuth(
    permissions,
    permissions.display !== 1 && permissions.soldOut !== 1
  );
});

const useMenuPermission = (storeId: string) => {
  const [menuPermission, setMenuPermission] = useRecoilState(menuPermissionState);
  const isNoAuthMenu = useRecoilValue(isAllMenuNoAuthSelector); // 메뉴관리 탭 비노출 유무
  const isNoAuthOpt = useRecoilValue(isAllOptNoAuthSelector); // 옵션관리 탭 비노출 유무
  const isNoAuthOrder = useRecoilValue(isAllOrderNoAuthSelector); // 메뉴순서 탭 비노출 유무
  const isNoAuthRecommend = useRecoilValue(isAllRecomNoAuthSelector); // 추천메뉴 탭 비노출 유무
  const isNoAuthOrigin = useRecoilValue(isAllOriginNoAuthSelector); // 원산지정보 탭 비노출 유무

  const isNoAuth1 = useRecoilValue(isNoAuthSelector);
  const isNoAuth2 = useRecoilValue(isNoAuthSelector2);
  const isNoAuth3 = useRecoilValue(isNoAuthSelector3);
  const isNoAuth4 = useRecoilValue(isNoAuthSelector4);
  const isNoAuth5 = useRecoilValue(isNoAuthSelector5);
  const isNoAuth6 = useRecoilValue(isNoAuthSelector6);
  const isNoAuth7 = useRecoilValue(isNoAuthSelector7);
  const isNoAuth8 = useRecoilValue(isNoAuthSelector8);
  const isNoAuth9 = useRecoilValue(isNoAuthSelector9);
  const isNoAuth10 = useRecoilValue(isNoAuthSelector10);
  const isNoAuth11 = useRecoilValue(isNoAuthSelector11);

  const { data, isLoading: isAuthLoading } = useQuery({
    queryKey: [PRODUCT_KEYS.PERMISSION, storeId], // storeId가 바뀌면 새로 불러옴
    queryFn: async () => {
      const response = await CallGetAuth(storeId);
      return response?.data?.menuPermission || [];
    },
    enabled: !!storeId, // storeId가 있을 때만 실행
  });

  useEffect(() => {
    if (data) {
      setMenuPermission({ data, isFetch: true });
    }
  }, [data, setMenuPermission]);

  return {
    menuPermission,
    isNoAuthMenu,
    isNoAuthOpt,
    isNoAuthOrder,
    isNoAuthRecommend,
    isNoAuthOrigin,
    isAuthLoading,
    isNoAuth1,
    isNoAuth2,
    isNoAuth3,
    isNoAuth4,
    isNoAuth5,
    isNoAuth6,
    isNoAuth7,
    isNoAuth8,
    isNoAuth9,
    isNoAuth10,
    isNoAuth11,
  };
};

export { useMenuPermission, isNoAuthSelector };
